// AGC: Quote PDF Service for generating supplier quote requests
// AGC: html2canvas renders the HTML template to a bitmap, then jsPDF wraps it into a
// AGC: real PDF so platform share intents (WhatsApp, email, Drive) get an application/pdf
// AGC: attachment instead of a PNG that browsers may reject or downgrade to plain text.

import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import type { BOMItem, PurchaseOrderSupplier } from '../types/inventory'
import { share as platformShare } from './shareService'

// AGC: Sanitize user input to prevent XSS when inserting into HTML templates
function escapeHtml(str: string | number | undefined | null): string {
  if (str == null) return ''
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// AGC: Load the real DroidFarm logo from /topmenulogo.png and cache as base64 data URL.
// AGC: html2canvas needs an inline data URL to render images in the offscreen template reliably.
let _logoDataUrl: string | null = null
async function getDroidFarmLogoDataUrl(): Promise<string> {
  if (_logoDataUrl) return _logoDataUrl
  try {
    const res = await fetch('/topmenulogo.png')
    const blob = await res.blob()
    _logoDataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
    return _logoDataUrl
  } catch {
    _logoDataUrl = ''
    return ''
  }
}

function logoImgTag(dataUrl: string, height: number): string {
  if (!dataUrl) return ''
  return `<img src="${dataUrl}" height="${height}" style="height: ${height}px; width: auto; object-fit: contain;" />`
}

export type QuoteFilterType = 'all' | 'low' | 'out'

// AGC: Quote line for builder modal (name, quantity, unit); maps to BOMItem for PDF
export interface QuoteLine {
  id: string
  inputId?: string
  inputName: string
  quantity: number
  unit: string
}

// AGC: Task option for quote builder – tasks that have at least one input with needToBuy > 0
export interface TaskQuoteOption {
  lotId: string
  lotName: string
  taskId: string
  taskTitle: string
  inputs: { inputId: string; inputName: string; needToBuy: number; unit: string }[]
}

// AGC: Map QuoteLine to BOMItem for existing quote PDF generation
export function quoteLineToBOMItem(line: QuoteLine): BOMItem {
  return {
    inputId: line.inputId ?? `custom-${line.id}`,
    inputName: line.inputName,
    category: 'other',
    unit: line.unit,
    requiredQuantity: line.quantity,
    currentStock: 0,
    needToBuy: line.quantity,
    stockStatus: 'out',
    estimatedCost: 0,
    tasks: [],
  }
}

export interface QuoteData {
  farmName: string
  farmLocation?: {
    lat: number
    lng: number
  }
  lotName: string
  lotArea: number
  items: BOMItem[]
  date: Date
  filterType: QuoteFilterType
  supplier?: PurchaseOrderSupplier
}

export interface QuoteTranslations {
  quoteRequest: string
  quoteSubtitle: string
  farmLabel: string
  locationLabel: string
  dateLabel: string
  generatedBy: string
  itemColumn: string
  qtyColumn: string
  unitColumn: string
  supplierInfo?: string
}

/**
 * AGC: Filter BOM items based on stock status
 */
export function filterBOMItems(items: BOMItem[], filterType: QuoteFilterType): BOMItem[] {
  switch (filterType) {
    case 'low':
      return items.filter(item => item.stockStatus === 'low' || item.stockStatus === 'critical')
    case 'out':
      return items.filter(item => item.stockStatus === 'out')
    case 'all':
    default:
      return items
  }
}

/**
 * AGC: Format GPS coordinates for display
 */
function formatCoordinates(lat: number, lng: number): string {
  const latDir = lat >= 0 ? 'N' : 'S'
  const lngDir = lng >= 0 ? 'E' : 'W'
  return `${Math.abs(lat).toFixed(6)}°${latDir}, ${Math.abs(lng).toFixed(6)}°${lngDir}`
}

/**
 * AGC: Format date for display
 */
function formatDate(date: Date, locale: string = 'pt-BR'): string {
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

/**
 * AGC: Generate HTML content for the quote
 */
export function generateQuoteHTML(data: QuoteData, translations: QuoteTranslations, logoDataUrl: string = ''): string {
  const filteredItems = filterBOMItems(data.items, data.filterType)
  
  const locationText = data.farmLocation 
    ? formatCoordinates(data.farmLocation.lat, data.farmLocation.lng)
    : 'N/A'
  
  const itemsHTML = filteredItems.map(item => `
    <tr>
      <td style="padding: 12px 16px; border-bottom: 1px solid #e5e7eb; font-size: 14px; color: #374151;">
        ${escapeHtml(item.inputName)}
      </td>
      <td style="padding: 12px 16px; border-bottom: 1px solid #e5e7eb; font-size: 14px; color: #374151; text-align: right; font-weight: 600;">
        ${escapeHtml(item.needToBuy.toFixed(2))}
      </td>
      <td style="padding: 12px 16px; border-bottom: 1px solid #e5e7eb; font-size: 14px; color: #6b7280; text-align: center;">
        ${escapeHtml(item.unit)}
      </td>
    </tr>
  `).join('')

  return `
    <div id="quote-content" style="
      width: 600px;
      background: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      padding: 32px;
      box-sizing: border-box;
    ">
      <!-- Header -->
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; padding-bottom: 24px; border-bottom: 2px solid #22c55e;">
        <div style="display: flex; align-items: center; gap: 16px;">
          ${logoImgTag(logoDataUrl, 50)}
          <div>
            <h1 style="margin: 0; font-size: 24px; font-weight: 700; color: #111827;">
              ${translations.quoteRequest}
            </h1>
            <p style="margin: 4px 0 0 0; font-size: 14px; color: #6b7280;">
              ${translations.quoteSubtitle}
            </p>
          </div>
        </div>
        <div style="text-align: right;">
          <p style="margin: 0; font-size: 12px; color: #9ca3af;">${translations.dateLabel}</p>
          <p style="margin: 4px 0 0 0; font-size: 14px; font-weight: 600; color: #374151;">
            ${formatDate(data.date)}
          </p>
        </div>
      </div>

      <!-- Farm Info -->
      <div style="background: #f9fafb; border-radius: 12px; padding: 20px; margin-bottom: 24px;">
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
          <div>
            <p style="margin: 0; font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px;">
              ${translations.farmLabel}
            </p>
            <p style="margin: 4px 0 0 0; font-size: 16px; font-weight: 600; color: #111827;">
              ${escapeHtml(data.farmName)}
            </p>
          </div>
          <div>
            <p style="margin: 0; font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px;">
              ${translations.locationLabel}
            </p>
            <p style="margin: 4px 0 0 0; font-size: 14px; color: #374151;">
              📍 ${locationText}
            </p>
          </div>
        </div>
        <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e5e7eb;">
          <p style="margin: 0; font-size: 13px; color: #6b7280;">
            <strong style="color: #374151;">Talhão:</strong> ${escapeHtml(data.lotName)} 
            <span style="margin-left: 16px;"><strong style="color: #374151;">Área:</strong> ${data.lotArea.toFixed(2)} ha</span>
          </p>
        </div>
      </div>

      <!-- Supplier Info (optional, for quote builder flow) -->
      ${data.supplier && (data.supplier.name || data.supplier.contact || data.supplier.phone || data.supplier.email) ? `
      <div style="background: #fef3c7; border-radius: 12px; padding: 16px; margin-bottom: 20px;">
        <h3 style="margin: 0 0 12px 0; font-size: 12px; font-weight: 600; color: #92400e; text-transform: uppercase; letter-spacing: 0.5px;">
          ${translations.supplierInfo ?? 'Fornecedor'}
        </h3>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
          ${data.supplier.name ? `
          <div>
            <p style="margin: 0; font-size: 11px; color: #92400e;">Fornecedor</p>
            <p style="margin: 2px 0 0 0; font-size: 14px; font-weight: 600; color: #78350f;">${escapeHtml(data.supplier.name)}</p>
          </div>
          ` : ''}
          ${data.supplier.contact ? `
          <div>
            <p style="margin: 0; font-size: 11px; color: #92400e;">Contato</p>
            <p style="margin: 2px 0 0 0; font-size: 13px; color: #78350f;">${escapeHtml(data.supplier.contact)}</p>
          </div>
          ` : ''}
          ${data.supplier.phone ? `
          <div>
            <p style="margin: 0; font-size: 11px; color: #92400e;">Telefone</p>
            <p style="margin: 2px 0 0 0; font-size: 13px; color: #78350f;">📞 ${escapeHtml(data.supplier.phone)}</p>
          </div>
          ` : ''}
          ${data.supplier.email ? `
          <div>
            <p style="margin: 0; font-size: 11px; color: #92400e;">E-mail</p>
            <p style="margin: 2px 0 0 0; font-size: 13px; color: #78350f;">✉️ ${escapeHtml(data.supplier.email)}</p>
          </div>
          ` : ''}
        </div>
      </div>
      ` : ''}

      <!-- Items Table -->
      <div style="border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden;">
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="background: #f3f4f6;">
              <th style="padding: 14px 16px; text-align: left; font-size: 12px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.5px;">
                ${translations.itemColumn}
              </th>
              <th style="padding: 14px 16px; text-align: right; font-size: 12px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.5px;">
                ${translations.qtyColumn}
              </th>
              <th style="padding: 14px 16px; text-align: center; font-size: 12px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.5px;">
                ${translations.unitColumn}
              </th>
            </tr>
          </thead>
          <tbody>
            ${itemsHTML}
          </tbody>
        </table>
      </div>

      <!-- Footer: DroidFarm branding -->
      <div style="margin-top: 32px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center;">
        <div style="display: inline-block;">
          ${logoImgTag(logoDataUrl, 36)}
          <p style="margin: 6px 0 0 0; font-size: 13px; font-weight: 700; color: #22c55e; letter-spacing: 0.5px;">
            Powered by DroidFarm
          </p>
          <p style="margin: 4px 0 0 0; font-size: 10px; color: #9ca3af;">
            ${translations.generatedBy}
          </p>
        </div>
      </div>
    </div>
  `
}

// AGC: Render any embedded HTML fragment (by element id) into a canvas and wrap it into
// AGC: a single-page A4 PDF. Used by both quote and purchase-order flows so there is one
// AGC: canonical place to evolve the template → PDF pipeline.
async function renderHtmlFragmentToCanvas(html: string, elementId: string): Promise<HTMLCanvasElement> {
  const container = document.createElement('div')
  container.style.position = 'absolute'
  container.style.left = '-9999px'
  container.style.top = '-9999px'
  container.innerHTML = html
  document.body.appendChild(container)
  try {
    await new Promise(resolve => setTimeout(resolve, 100))
    const element = container.querySelector(`#${elementId}`) as HTMLElement | null
    if (!element) throw new Error(`Fragment #${elementId} not found`)
    return await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      logging: false,
    })
  } finally {
    document.body.removeChild(container)
  }
}

function canvasToPdfBlob(canvas: HTMLCanvasElement): Blob {
  const imgData = canvas.toDataURL('image/jpeg', 0.92)
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 10
  const usableWidth = pageWidth - margin * 2
  const ratio = canvas.height / canvas.width
  const imgHeight = usableWidth * ratio
  if (imgHeight <= pageHeight - margin * 2) {
    pdf.addImage(imgData, 'JPEG', margin, margin, usableWidth, imgHeight)
  } else {
    // AGC: Paginate if the rendered fragment is taller than one A4 page.
    const pageContentHeight = pageHeight - margin * 2
    const pxPerMm = canvas.width / usableWidth
    const pageSliceHeightPx = pageContentHeight * pxPerMm
    let offsetPx = 0
    let isFirst = true
    while (offsetPx < canvas.height) {
      const sliceHeight = Math.min(pageSliceHeightPx, canvas.height - offsetPx)
      const sliceCanvas = document.createElement('canvas')
      sliceCanvas.width = canvas.width
      sliceCanvas.height = sliceHeight
      const ctx = sliceCanvas.getContext('2d')
      if (!ctx) throw new Error('Canvas 2D context unavailable')
      ctx.drawImage(canvas, 0, offsetPx, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight)
      const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.92)
      const sliceMm = sliceHeight / pxPerMm
      if (!isFirst) pdf.addPage()
      pdf.addImage(sliceData, 'JPEG', margin, margin, usableWidth, sliceMm)
      isFirst = false
      offsetPx += sliceHeight
    }
  }
  return pdf.output('blob')
}

/**
 * AGC: Generate quote image using html2canvas
 */
export async function generateQuoteImage(data: QuoteData, translations: QuoteTranslations): Promise<Blob> {
  const logoDataUrl = await getDroidFarmLogoDataUrl()
  const container = document.createElement('div')
  container.style.position = 'absolute'
  container.style.left = '-9999px'
  container.style.top = '-9999px'
  container.innerHTML = generateQuoteHTML(data, translations, logoDataUrl)
  document.body.appendChild(container)

  try {
    // AGC: Wait for SVG to render
    await new Promise(resolve => setTimeout(resolve, 100))
    
    const element = container.querySelector('#quote-content') as HTMLElement
    if (!element) {
      throw new Error('Quote content element not found')
    }

    // AGC: Generate canvas from HTML
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2, // AGC: Higher resolution
      useCORS: true,
      logging: false,
    })

    // AGC: Convert canvas to blob
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to generate image blob'))
          }
        },
        'image/png',
        1.0
      )
    })
  } finally {
    // AGC: Clean up
    document.body.removeChild(container)
  }
}

/**
 * AGC: Generate real PDF bytes for a quote.
 */
export async function generateQuotePdf(data: QuoteData, translations: QuoteTranslations): Promise<Blob> {
  const logoDataUrl = await getDroidFarmLogoDataUrl()
  const canvas = await renderHtmlFragmentToCanvas(
    generateQuoteHTML(data, translations, logoDataUrl),
    'quote-content'
  )
  return canvasToPdfBlob(canvas)
}

function slugForFileName(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase()
    .slice(0, 48) || 'droidfarm'
}

/**
 * AGC: Share quote as a real PDF. Uses native share intent on Capacitor, Web Share API
 * AGC: on supported browsers, falls back to anchor download otherwise.
 */
export async function shareQuote(
  data: QuoteData,
  translations: QuoteTranslations
): Promise<{ success: boolean; method: 'share' | 'download' }> {
  const pdfBlob = await generateQuotePdf(data, translations)
  const fileName = `cotacao-${slugForFileName(data.farmName)}-${Date.now()}.pdf`
  const result = await platformShare({
    title: translations.quoteRequest,
    text: `${translations.quoteRequest} — ${data.farmName}`,
    dialogTitle: translations.quoteRequest,
    file: { data: pdfBlob, fileName, mimeType: 'application/pdf' },
  })
  if (!result.success) return { success: false, method: 'share' }
  return { success: true, method: result.method === 'web-download' ? 'download' : 'share' }
}

/**
 * AGC: Generate quote as text for clipboard/messaging
 */
export function generateQuoteText(data: QuoteData, translations: QuoteTranslations): string {
  const filteredItems = filterBOMItems(data.items, data.filterType)
  const locationText = data.farmLocation 
    ? formatCoordinates(data.farmLocation.lat, data.farmLocation.lng)
    : 'N/A'
  
  const itemsList = filteredItems
    .map(item => `• ${item.inputName}: ${item.needToBuy.toFixed(2)} ${item.unit}`)
    .join('\n')

  return `
🌾 ${translations.quoteRequest.toUpperCase()}
━━━━━━━━━━━━━━━━━━━━━━

📍 ${translations.farmLabel}: ${escapeHtml(data.farmName)}
🗺️ ${translations.locationLabel}: ${locationText}
📅 ${translations.dateLabel}: ${formatDate(data.date)}
🌱 Talhão: ${escapeHtml(data.lotName)} (${data.lotArea.toFixed(2)} ha)

━━━━━━━━━━━━━━━━━━━━━━
📦 ITENS SOLICITADOS:
━━━━━━━━━━━━━━━━━━━━━━

${itemsList}

━━━━━━━━━━━━━━━━━━━━━━
${translations.generatedBy} 🌱
  `.trim()
}

/**
 * AGC: Copy quote text to clipboard
 */
export async function copyQuoteToClipboard(data: QuoteData, translations: QuoteTranslations): Promise<void> {
  const text = generateQuoteText(data, translations)
  await navigator.clipboard.writeText(text)
}

// =============================================================================
// AGC: PURCHASE ORDER PDF GENERATION
// =============================================================================

import type { PurchaseOrder } from '../types/inventory'

export interface PurchaseOrderPDFTranslations {
  title: string
  subtitle: string
  farmInfo: string
  supplierInfo: string
  itemsTable: string
  generatedBy: string
  deliveryAddress: string
  contactInfo: string
  orderNumber: string
  orderDate: string
  expectedDelivery: string
  itemColumn: string
  qtyColumn: string
  unitColumn: string
  lotsColumn: string
  totalItems: string
  estimatedCost: string
  notes: string
}

export interface PurchaseOrderPDFData {
  order: PurchaseOrder
  farmName: string
  farmLocation?: string
  contactPhone?: string
  contactEmail?: string
}

/**
 * AGC: Generate HTML for purchase order PDF
 */
export function generatePurchaseOrderHTML(
  data: PurchaseOrderPDFData,
  translations: PurchaseOrderPDFTranslations,
  logoDataUrl: string = '',
): string {
  const order = data.order
  
  const itemsHTML = order.items.map(item => {
    const lotsText = item.lotsNeeding.length > 0 
      ? item.lotsNeeding.map(l => l.lotName).join(', ')
      : '-'
    
    return `
      <tr>
        <td style="padding: 10px 14px; border-bottom: 1px solid #e5e7eb; font-size: 13px; color: #374151;">
          ${escapeHtml(item.inputName)}
        </td>
        <td style="padding: 10px 14px; border-bottom: 1px solid #e5e7eb; font-size: 13px; color: #374151; text-align: right; font-weight: 600;">
          ${escapeHtml(item.requestedQuantity)}
        </td>
        <td style="padding: 10px 14px; border-bottom: 1px solid #e5e7eb; font-size: 13px; color: #6b7280; text-align: center;">
          ${escapeHtml(item.unit)}
        </td>
        <td style="padding: 10px 14px; border-bottom: 1px solid #e5e7eb; font-size: 11px; color: #6b7280; max-width: 150px;">
          ${lotsText}
        </td>
      </tr>
    `
  }).join('')

  const totalCost = order.totalEstimatedCost > 0 
    ? `R$ ${order.totalEstimatedCost.toFixed(2)}` 
    : '-'

  const supplierSection = order.supplier ? `
    <div style="background: #fef3c7; border-radius: 12px; padding: 16px; margin-bottom: 20px;">
      <h3 style="margin: 0 0 12px 0; font-size: 12px; font-weight: 600; color: #92400e; text-transform: uppercase; letter-spacing: 0.5px;">
        ${translations.supplierInfo}
      </h3>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
        ${order.supplier.name ? `
          <div>
            <p style="margin: 0; font-size: 11px; color: #92400e;">Fornecedor</p>
            <p style="margin: 2px 0 0 0; font-size: 14px; font-weight: 600; color: #78350f;">${escapeHtml(order.supplier.name)}</p>
          </div>
        ` : ''}
        ${order.supplier.contact ? `
          <div>
            <p style="margin: 0; font-size: 11px; color: #92400e;">Contato</p>
            <p style="margin: 2px 0 0 0; font-size: 13px; color: #78350f;">${escapeHtml(order.supplier.contact)}</p>
          </div>
        ` : ''}
        ${order.supplier.phone ? `
          <div>
            <p style="margin: 0; font-size: 11px; color: #92400e;">Telefone</p>
            <p style="margin: 2px 0 0 0; font-size: 13px; color: #78350f;">📞 ${escapeHtml(order.supplier.phone)}</p>
          </div>
        ` : ''}
        ${order.supplier.email ? `
          <div>
            <p style="margin: 0; font-size: 11px; color: #92400e;">E-mail</p>
            <p style="margin: 2px 0 0 0; font-size: 13px; color: #78350f;">✉️ ${escapeHtml(order.supplier.email)}</p>
          </div>
        ` : ''}
      </div>
    </div>
  ` : ''

  return `
    <div id="order-content" style="
      width: 650px;
      background: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      padding: 28px;
      box-sizing: border-box;
    ">
      <!-- Header -->
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 3px solid #22c55e;">
        <div style="display: flex; align-items: center; gap: 14px;">
          ${logoImgTag(logoDataUrl, 50)}
          <div>
            <h1 style="margin: 0; font-size: 22px; font-weight: 700; color: #111827;">
              ${translations.title}
            </h1>
            <p style="margin: 4px 0 0 0; font-size: 13px; color: #6b7280;">
              ${translations.subtitle}
            </p>
          </div>
        </div>
        <div style="text-align: right; background: #f0fdf4; padding: 10px 16px; border-radius: 8px;">
          <p style="margin: 0; font-size: 11px; color: #16a34a; text-transform: uppercase; letter-spacing: 0.5px;">
            ${translations.orderNumber}
          </p>
          <p style="margin: 4px 0 0 0; font-size: 18px; font-weight: 700; color: #15803d;">
            ${order.orderNumber}
          </p>
        </div>
      </div>

      <!-- Order Info -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px;">
        <div style="background: #f9fafb; border-radius: 10px; padding: 14px;">
          <h3 style="margin: 0 0 10px 0; font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px;">
            ${translations.farmInfo}
          </h3>
          <p style="margin: 0; font-size: 15px; font-weight: 600; color: #111827;">${escapeHtml(data.farmName)}</p>
          ${data.farmLocation ? `<p style="margin: 4px 0 0 0; font-size: 12px; color: #6b7280;">📍 ${data.farmLocation}</p>` : ''}
          ${data.contactPhone ? `<p style="margin: 4px 0 0 0; font-size: 12px; color: #6b7280;">📞 ${data.contactPhone}</p>` : ''}
          ${data.contactEmail ? `<p style="margin: 4px 0 0 0; font-size: 12px; color: #6b7280;">✉️ ${data.contactEmail}</p>` : ''}
        </div>
        
        <div style="background: #f9fafb; border-radius: 10px; padding: 14px;">
          <h3 style="margin: 0 0 10px 0; font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px;">
            Informações do Pedido
          </h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
            <div>
              <p style="margin: 0; font-size: 10px; color: #9ca3af;">${translations.orderDate}</p>
              <p style="margin: 2px 0 0 0; font-size: 12px; font-weight: 500; color: #374151;">
                ${new Date(order.createdAt).toLocaleDateString('pt-BR')}
              </p>
            </div>
            ${order.expectedDeliveryDate ? `
              <div>
                <p style="margin: 0; font-size: 10px; color: #9ca3af;">${translations.expectedDelivery}</p>
                <p style="margin: 2px 0 0 0; font-size: 12px; font-weight: 500; color: #374151;">
                  ${new Date(order.expectedDeliveryDate).toLocaleDateString('pt-BR')}
                </p>
              </div>
            ` : ''}
            <div>
              <p style="margin: 0; font-size: 10px; color: #9ca3af;">${translations.totalItems}</p>
              <p style="margin: 2px 0 0 0; font-size: 12px; font-weight: 500; color: #374151;">
                ${order.items.length} itens
              </p>
            </div>
            <div>
              <p style="margin: 0; font-size: 10px; color: #9ca3af;">${translations.estimatedCost}</p>
              <p style="margin: 2px 0 0 0; font-size: 12px; font-weight: 600; color: #16a34a;">
                ${totalCost}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Supplier Info -->
      ${supplierSection}

      <!-- Items Table -->
      <div style="border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; margin-bottom: 16px;">
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="background: #f3f4f6;">
              <th style="padding: 12px 14px; text-align: left; font-size: 11px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.5px;">
                ${translations.itemColumn}
              </th>
              <th style="padding: 12px 14px; text-align: right; font-size: 11px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.5px;">
                ${translations.qtyColumn}
              </th>
              <th style="padding: 12px 14px; text-align: center; font-size: 11px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.5px;">
                ${translations.unitColumn}
              </th>
              <th style="padding: 12px 14px; text-align: left; font-size: 11px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.5px;">
                ${translations.lotsColumn}
              </th>
            </tr>
          </thead>
          <tbody>
            ${itemsHTML}
          </tbody>
        </table>
      </div>

      <!-- Notes -->
      ${order.notes ? `
        <div style="background: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px; padding: 12px; margin-bottom: 16px;">
          <p style="margin: 0 0 6px 0; font-size: 11px; font-weight: 600; color: #92400e; text-transform: uppercase;">
            ${translations.notes}
          </p>
          <p style="margin: 0; font-size: 12px; color: #78350f; white-space: pre-wrap;">
            ${escapeHtml(order.notes)}
          </p>
        </div>
      ` : ''}

      <!-- Footer: DroidFarm branding -->
      <div style="margin-top: 28px; padding-top: 18px; border-top: 1px solid #e5e7eb; text-align: center;">
        <div style="display: inline-block;">
          ${logoImgTag(logoDataUrl, 36)}
          <p style="margin: 6px 0 0 0; font-size: 13px; font-weight: 700; color: #22c55e; letter-spacing: 0.5px;">
            Powered by DroidFarm
          </p>
          <p style="margin: 4px 0 0 0; font-size: 10px; color: #9ca3af;">
            ${translations.generatedBy}
          </p>
        </div>
      </div>
    </div>
  `
}

/**
 * AGC: Generate purchase order image (PNG blob) — kept for any legacy embed use.
 */
export async function generatePurchaseOrderImage(
  data: PurchaseOrderPDFData,
  translations: PurchaseOrderPDFTranslations
): Promise<Blob> {
  const logoDataUrl = await getDroidFarmLogoDataUrl()
  const canvas = await renderHtmlFragmentToCanvas(
    generatePurchaseOrderHTML(data, translations, logoDataUrl),
    'order-content'
  )
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new Error('Failed to generate image blob'))),
      'image/png',
      1.0
    )
  })
}

/**
 * AGC: Generate real PDF bytes for a purchase order.
 */
export async function generatePurchaseOrderPdf(
  data: PurchaseOrderPDFData,
  translations: PurchaseOrderPDFTranslations
): Promise<Blob> {
  const logoDataUrl = await getDroidFarmLogoDataUrl()
  const canvas = await renderHtmlFragmentToCanvas(
    generatePurchaseOrderHTML(data, translations, logoDataUrl),
    'order-content'
  )
  return canvasToPdfBlob(canvas)
}

/**
 * AGC: Share purchase order as a real PDF using the platform share service.
 */
export async function sharePurchaseOrder(
  data: PurchaseOrderPDFData,
  translations: PurchaseOrderPDFTranslations
): Promise<{ success: boolean; method: 'share' | 'download' }> {
  const pdfBlob = await generatePurchaseOrderPdf(data, translations)
  const fileName = `pedido-${data.order.orderNumber}-${Date.now()}.pdf`
  const result = await platformShare({
    title: `${translations.title} — ${data.order.orderNumber}`,
    text: `${translations.title} ${data.order.orderNumber} — ${data.farmName}`,
    dialogTitle: translations.title,
    file: { data: pdfBlob, fileName, mimeType: 'application/pdf' },
  })
  if (!result.success) return { success: false, method: 'share' }
  return { success: true, method: result.method === 'web-download' ? 'download' : 'share' }
}

/**
 * AGC: Generate purchase order as text for clipboard/WhatsApp
 */
export function generatePurchaseOrderText(data: PurchaseOrderPDFData): string {
  const order = data.order
  
  const itemsList = order.items
    .map(item => {
      const lotsText = item.lotsNeeding.length > 0 
        ? ` (para: ${item.lotsNeeding.map(l => l.lotName).join(', ')})`
        : ''
      return `• ${item.inputName}: ${item.requestedQuantity} ${item.unit}${lotsText}`
    })
    .join('\n')

  const supplierInfo = order.supplier ? `
📦 FORNECEDOR:
• Nome: ${order.supplier.name || '-'}
${order.supplier.contact ? `• Contato: ${escapeHtml(order.supplier.contact)}` : ''}
${order.supplier.phone ? `• Telefone: ${escapeHtml(order.supplier.phone)}` : ''}
${order.supplier.email ? `• E-mail: ${escapeHtml(order.supplier.email)}` : ''}
` : ''

  const totalCost = order.totalEstimatedCost > 0 
    ? `💰 Custo Estimado: R$ ${order.totalEstimatedCost.toFixed(2)}\n` 
    : ''

  return `
🌾 PEDIDO DE COMPRA
━━━━━━━━━━━━━━━━━━━━━━
📋 Nº: ${order.orderNumber}
📅 Data: ${new Date(order.createdAt).toLocaleDateString('pt-BR')}
${order.expectedDeliveryDate ? `🚚 Entrega Prevista: ${new Date(order.expectedDeliveryDate).toLocaleDateString('pt-BR')}\n` : ''}
🏡 Fazenda: ${escapeHtml(data.farmName)}
${data.farmLocation ? `📍 Local: ${data.farmLocation}\n` : ''}
${supplierInfo}
━━━━━━━━━━━━━━━━━━━━━━
📦 ITENS DO PEDIDO:
━━━━━━━━━━━━━━━━━━━━━━

${itemsList}

━━━━━━━━━━━━━━━━━━━━━━
📊 Total: ${order.items.length} itens
${totalCost}${order.notes ? `📝 Obs: ${escapeHtml(order.notes)}\n` : ''}
━━━━━━━━━━━━━━━━━━━━━━
Gerado por DroidFarm 🌱
  `.trim()
}

/**
 * AGC: Copy purchase order text to clipboard
 */
export async function copyPurchaseOrderToClipboard(data: PurchaseOrderPDFData): Promise<void> {
  const text = generatePurchaseOrderText(data)
  await navigator.clipboard.writeText(text)
}

/**
 * AGC: Get default translations for purchase order PDF
 */
export function getDefaultPurchaseOrderTranslations(language: 'pt' | 'en' | 'es' = 'pt'): PurchaseOrderPDFTranslations {
  const translations: Record<string, PurchaseOrderPDFTranslations> = {
    pt: {
      title: 'Pedido de Compra',
      subtitle: 'Solicitação de Insumos Agrícolas',
      farmInfo: 'Informações da Fazenda',
      supplierInfo: 'Informações do Fornecedor',
      itemsTable: 'Itens',
      generatedBy: 'Gerado por DroidFarm',
      deliveryAddress: 'Endereço de Entrega',
      contactInfo: 'Contato para Confirmação',
      orderNumber: 'Pedido Nº',
      orderDate: 'Data do Pedido',
      expectedDelivery: 'Entrega Prevista',
      itemColumn: 'Item',
      qtyColumn: 'Qtd',
      unitColumn: 'Unidade',
      lotsColumn: 'Lotes',
      totalItems: 'Total de Itens',
      estimatedCost: 'Custo Estimado',
      notes: 'Observações',
    },
    en: {
      title: 'Purchase Order',
      subtitle: 'Agricultural Supplies Request',
      farmInfo: 'Farm Information',
      supplierInfo: 'Supplier Information',
      itemsTable: 'Items',
      generatedBy: 'Generated by DroidFarm',
      deliveryAddress: 'Delivery Address',
      contactInfo: 'Contact for Confirmation',
      orderNumber: 'Order #',
      orderDate: 'Order Date',
      expectedDelivery: 'Expected Delivery',
      itemColumn: 'Item',
      qtyColumn: 'Qty',
      unitColumn: 'Unit',
      lotsColumn: 'Lots',
      totalItems: 'Total Items',
      estimatedCost: 'Estimated Cost',
      notes: 'Notes',
    },
    es: {
      title: 'Pedido de Compra',
      subtitle: 'Solicitud de Insumos Agrícolas',
      farmInfo: 'Información de la Finca',
      supplierInfo: 'Información del Proveedor',
      itemsTable: 'Items',
      generatedBy: 'Generado por DroidFarm',
      deliveryAddress: 'Dirección de Entrega',
      contactInfo: 'Contacto para Confirmación',
      orderNumber: 'Pedido #',
      orderDate: 'Fecha del Pedido',
      expectedDelivery: 'Entrega Esperada',
      itemColumn: 'Item',
      qtyColumn: 'Cant',
      unitColumn: 'Unidad',
      lotsColumn: 'Lotes',
      totalItems: 'Total de Items',
      estimatedCost: 'Costo Estimado',
      notes: 'Notas',
    },
  }
  
  return translations[language] ?? translations.pt!
}